import { useState, type FormEvent } from 'react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { news } from '@/mocks/news';
import { works } from '@/mocks/works';

const values = [
  {
    icon: 'ri-radar-line',
    title: '资讯为先',
    desc: '第一时间汇集影坛与剧集圈的新鲜动态，让你不错过每一件值得关注的事。',
  },
  {
    icon: 'ri-movie-2-line',
    title: '内容精选',
    desc: '以扎实的筛选标准挑选作品，从热播大剧到口碑佳作，帮你看得更省心。',
  },
  {
    icon: 'ri-compass-3-line',
    title: '发现热爱',
    desc: '通过分类、搜索与推荐联动，让每一次浏览都更容易遇见“下一部心头好”。',
  },
  {
    icon: 'ri-shield-star-line',
    title: '专业可信',
    desc: '坚持真实、清晰的呈现方式，只做影视信息的整理与推荐，不夸大、不误导。',
  },
];

const contactItems = [
  { icon: 'ri-mail-line', label: '邮箱', value: 'hello@fengyuling.demo' },
  { icon: 'ri-wechat-line', label: '微信公众号', value: '枫与铃影视' },
  { icon: 'ri-map-pin-line', label: '办公地址', value: '上海市 · 静安区影视文创园' },
  { icon: 'ri-time-line', label: '服务时间', value: '周一至周五 09:30 - 18:00' },
];

export default function About() {
  usePageMeta(
    '关于我们 - 枫与铃',
    '枫与铃致力于提供最新影视剧资讯和推荐，涵盖电影、电视剧等多种影视作品，帮助用户快速了解和选择喜爱的影视内容。',
  );

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/db7sv2f3hiaolq192lfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试。');
    }
  };

  const stats = [
    { label: '收录影视作品', value: `${works.length}+` },
    { label: '影视资讯', value: `${news.length}+` },
    { label: '覆盖地区', value: '6+' },
  ];

  return (
    <div className="w-full">
      <div className="w-full bg-primary-900 py-12 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <span className="inline-block rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            关于我们
          </span>
          <h1 className="mt-4 font-heading text-3xl font-black text-background-50 md:text-4xl">
            枫与铃，你打开影视世界的第一站
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-primary-100 md:text-base">
            枫与铃致力于提供最新影视剧资讯和推荐，涵盖电影、电视剧等多种影视作品，帮助用户快速了解和选择喜爱的影视内容。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">网站介绍</h2>
            <div className="mt-4 space-y-4 text-sm leading-loose text-foreground-800 md:text-base">
              <p>
                枫与铃是一个以影视剧资讯与推荐为核心的影视门户。我们希望把分散在各处的影视信息整理得清晰、好读，让普通观众和资深影迷都能在最短的时间内，找到自己真正想看的内容。
              </p>
              <p>
                站内内容围绕「最新影视资讯」「电影与电视剧介绍」「热门影视推荐」三大方向展开。你可以按分类筛选作品，也可以通过关键词搜索，还可以在详情页顺着相关推荐不断发现新的作品。
              </p>
              <p>
                目前站内所有内容均为演示示例，主要用于展示产品形态与浏览体验，不提供在线播放、下载、会员或评论等功能。我们尊重版权，也期待在合规的前提下，把更好的影视信息服务带给大家。
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 lg:col-span-1 lg:grid-cols-1">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center lg:text-left">
                <p className="font-heading text-2xl font-black text-primary-600 lg:text-3xl">{s.value}</p>
                <p className="mt-1 text-xs text-foreground-600 md:text-sm">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们的理念</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className={`${v.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
            <p className="mt-3 text-sm text-foreground-600">欢迎通过以下方式与我们取得联系，也可以在右侧留言。</p>
            <ul className="mt-6 space-y-4">
              {contactItems.map((c) => (
                <li key={c.label} className="flex items-center gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${c.icon} text-lg`} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="text-sm text-foreground-900">{c.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">留言给我们</h2>
            <form
              data-readdy-form
              id="about-contact-form"
              onSubmit={handleSubmit}
              className="mt-5 space-y-4"
            >
              <div>
                <label htmlFor="about-name" className="mb-1.5 block text-sm text-foreground-700">
                  姓名
                </label>
                <input
                  id="about-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                />
              </div>
              <div>
                <label htmlFor="about-email" className="mb-1.5 block text-sm text-foreground-700">
                  邮箱
                </label>
                <input
                  id="about-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收我们的回复"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                />
              </div>
              <div>
                <label htmlFor="about-message" className="mb-1.5 block text-sm text-foreground-700">
                  留言内容
                </label>
                <textarea
                  id="about-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="想说的话、想推荐的作品，都可以在这里告诉我们（500 字以内）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                className="brand-contact-guard"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    提交留言
                  </>
                )}
              </button>

              {status === 'success' && (
                <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-line" />
                  留言已成功提交，感谢你的反馈，我们会尽快与你联系。
                </p>
              )}
              {status === 'error' && (
                <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-800">
                  <i className="ri-error-warning-line" />
                  {formError || '提交失败，请稍后重试。'}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">法律声明与版权信息</h2>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-700">
            <p>
              本站所有影视名称、简介及相关信息均为演示用途的虚拟示例，不指向任何真实作品或版权方。站内所使用的配图均为示意图片，不代表真实影视作品宣传物料。
            </p>
            <p>
              枫与铃不提供在线播放、下载、会员与支付服务，亦不存储任何影视资源。若你对本站内容有任何疑问或建议，欢迎通过上方联系方式与我们沟通。
            </p>
            <p>© 2025 枫与铃 · 影视剧资讯与精彩推荐　保留所有权利。</p>
          </div>
        </div>
      </div>
    </div>
  );
}